import { ComponentProps } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button } from "@design-system/Button/Button";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { MultiLineTextField } from "@design-system/MultiLineTextField/MultiLineTextField";
import { TextField } from "@design-system/TextField/TextField";

export type ClientApplicationFormValues = {
  productName: string;
  productDomainName: string;
  productDescription: string;
  vendorName: string;
  vendorDomainName: string;
  vendorDescription: string;
  usedBy: string;
};

type Props = {
  submitText?: string;
  onSubmit: SubmitHandler<ClientApplicationFormValues>;
  values?: ClientApplicationFormValues;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
  disabled?: boolean;
  withSubmitButton?: boolean;
};

export const ClientApplicationForm = ({
  submitText = "Submit",
  onSubmit,
  values,
  errorsAPI,
  disabled,
  withSubmitButton = true,
}: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<ClientApplicationFormValues>({
    values: {
      productName: values?.productName ?? "",
      productDomainName: values?.productDomainName ?? "",
      productDescription: values?.productDescription ?? "",
      vendorName: values?.vendorName ?? "",
      vendorDomainName: values?.vendorDomainName ?? "",
      vendorDescription: values?.vendorDescription ?? "",
      usedBy: values?.usedBy ?? "",
    },
  });

  return (
    <Form<ClientApplicationFormValues>
      onSubmit={handleSubmit(onSubmit)}
      setError={setError}
      errorsAPI={errorsAPI}
    >
      <FieldGroup label="Product name" error={errors.productName?.message}>
        <TextField
          type="text"
          {...register("productName")}
          disabled={disabled}
        />
      </FieldGroup>

      <FieldGroup
        label="Product domain"
        error={errors.productDomainName?.message}
      >
        <TextField
          type="text"
          {...register("productDomainName")}
          disabled={disabled}
        />
      </FieldGroup>

      <FieldGroup
        label="Product description"
        error={errors.productDescription?.message}
      >
        <MultiLineTextField
          {...register("productDescription")}
          disabled={disabled}
        />
      </FieldGroup>

      <FieldGroup label="Vendor name" error={errors.vendorName?.message}>
        <TextField
          type="text"
          {...register("vendorName")}
          disabled={disabled}
        />
      </FieldGroup>

      <FieldGroup
        label="Vendor domain"
        error={errors.vendorDomainName?.message}
      >
        <TextField
          type="text"
          {...register("vendorDomainName")}
          disabled={disabled}
        />
      </FieldGroup>

      <FieldGroup
        label="Vendor description"
        error={errors.vendorDescription?.message}
      >
        <MultiLineTextField
          {...register("vendorDescription")}
          disabled={disabled}
        />
      </FieldGroup>

      <FieldGroup label="Used by" error={errors.usedBy?.message}>
        <TextField type="text" {...register("usedBy")} disabled={disabled} />
      </FieldGroup>

      {withSubmitButton && (
        <Button isLoading={isSubmitting} type="submit">
          {submitText}
        </Button>
      )}
    </Form>
  );
};
